import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Eye, KeyRound, Link2, Loader2, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import DonutChart from '../components/DonutChart';
import { ApiService } from '../utils/api';
import { useEventRoute } from '../utils/eventRef';
import { conditionsSearch, STATUS_LABELS } from '../utils/registrationFilterParams';
import { downloadAccessCodeRegistrations, downloadInviteRegistrations } from '../utils/inviteExport';
import { useToast } from '../components/Toast';
import { formatDateTime } from '../utils/datetime';
import { useAuth } from '../contexts/AuthContext';
import { resolveRoleUi } from '../utils/roleUi';

const MIN_SEARCH = 4;

/** One row per invite group (institute): limit, usage, codes; drill into its registrations or download them. */
const AccessCodesPage = () => {
  const { eventId, eventRef } = useEventRoute();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(null);

  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });
  const { data: stats = [], isLoading } = useQuery({
    queryKey: ['inviteLinks', eventId],
    queryFn: () => ApiService.getInviteLinks(eventId),
    enabled: !!eventId,
    refetchInterval: 30000,
  });

  const { data: ticketing } = useQuery({
    queryKey: ['ticketingConfig', eventId],
    queryFn: () => ApiService.getTicketingConfig(eventId).catch(() => null),
    enabled: !!eventId,
    staleTime: 60 * 1000,
  });
  const passName = (id) => ticketing?.passes?.find((p) => p.id === id)?.name || id;

  const { user } = useAuth();
  const ui = resolveRoleUi(event?.roleUi, user?.roles || []);

  const term = query.trim().toLowerCase();
  const rows = useMemo(() => {
    if (term.length < MIN_SEARCH) return stats;
    return stats.filter(({ link }) => (link.name || '').toLowerCase().includes(term)
      || (link.code || '').toLowerCase().includes(term)
      || (link.accessCodes || []).some((c) => c.code.toLowerCase().includes(term) || (c.label || '').toLowerCase().includes(term)));
  }, [stats, term]);

  const canOpenRegistrations = ui.can('registrationsPage');
  const registrationsUrl = (target) => `/admin/events/${eventRef}/registrations?${conditionsSearch([target])}`;
  const totalUsed = stats.reduce((sum, { link }) => sum + (link.usedCount || 0), 0);
  const totalCapacity = stats.reduce((sum, { link }) => sum + (link.maxUses || 0), 0);
  const unlimited = stats.some(({ link }) => !link.maxUses);

  const downloadCode = async (link, code) => {
    const key = `${link.id}:${code.code}`;
    setBusy(key);
    try {
      await downloadAccessCodeRegistrations(eventId, code.code);
      showSuccess(`Downloading ${code.usedCount} registrations that used ${code.code}`);
    } catch (err) {
      showError(err.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const download = async (link) => {
    setBusy(link ? link.id : 'all');
    try {
      await downloadInviteRegistrations(eventId, link?.id);
      showSuccess(link ? `Downloading ${link.usedCount} registrations from ${link.name}` : 'Downloading every invited registration');
    } catch (err) {
      showError(err.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (value && value.length < MIN_SEARCH) {
      showError(`Type at least ${MIN_SEARCH} characters`);
      return;
    }
    setQuery(value);
  };

  const groupSlices = stats.map(({ link, registrations }) => ({
    key: link.id,
    label: link.name,
    count: registrations,
    onSelect: canOpenRegistrations ? () => navigate(registrationsUrl({ type: 'invite', value: link.id, name: link.name })) : undefined,
  }));
  const codeSlices = stats.flatMap(({ link }) => (link.accessCodes || []).map((code) => ({
    key: `${link.id}:${code.code}`,
    label: `${code.code}${code.label ? ` · ${code.label}` : ''} (${link.name})`,
    count: code.usedCount || 0,
    onSelect: canOpenRegistrations ? () => navigate(registrationsUrl({ type: 'field', field: 'Access Code', value: code.code })) : undefined,
  })));
  const statusTotals = {};
  stats.forEach(({ byStatus }) => Object.entries(byStatus || {}).forEach(([status, count]) => {
    statusTotals[status] = (statusTotals[status] || 0) + count;
  }));
  const statusSlices = Object.entries(statusTotals).map(([status, count]) => ({
    key: status,
    label: STATUS_LABELS[status] || status,
    count,
  }));

  if (event && !ui.can('accessCodesPage')) {
    return <p className="p-8 text-center text-gray-600">This page isn't available for your role.</p>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          {ui.can('registrationsPage') && (
            <Link to={`/admin/events/${eventRef}/registrations`} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
              <ArrowLeft className="mr-1 h-4 w-4" /> Registrations
            </Link>
          )}
          <h1 className="mt-1 truncate text-2xl font-bold text-gray-900 sm:text-3xl">{event?.name || 'Event'}</h1>
          <p className="text-sm text-gray-500">Access codes &amp; invited groups</p>
        </div>
        <Button onClick={() => download(null)} disabled={busy === 'all' || totalUsed === 0} className="w-full sm:w-auto">
          {busy === 'all' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Export all invited (Excel)
        </Button>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Groups', stats.length],
          ['Access codes', stats.reduce((sum, { link }) => sum + (link.accessCodes?.length || 0), 0)],
          ['Registered via invites', totalUsed],
          ['Places allowed', unlimited ? 'Unlimited' : totalCapacity],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border bg-white p-4">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
            <dd className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</dd>
          </div>
        ))}
      </dl>

      {totalUsed > 0 && ui.can('analytics') && (
        <div className="grid gap-4 lg:grid-cols-3">
          <DonutChart title="Registrations by group" slices={groupSlices} footnote={canOpenRegistrations ? 'Select a group to open its registrations.' : undefined} />
          <DonutChart title="Registrations by access code" slices={codeSlices} emptyText="No codes used yet" />
          <DonutChart title="Status of invited registrations" slices={statusSlices} />
        </div>
      )}

      <section className="rounded-xl border bg-white">
        <form onSubmit={submit} className="flex flex-col gap-2 border-b p-3 sm:flex-row sm:p-4" role="search">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={input}
              onChange={(e) => { setInput(e.target.value); if (!e.target.value) setQuery(''); }}
              placeholder={`Search by institute or access code (min ${MIN_SEARCH} characters)`}
              className="h-10 w-full rounded-md border border-gray-300 pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Search groups and access codes"
            />
            {input && (
              <button type="button" onClick={() => { setInput(''); setQuery(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400" aria-label="Clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button type="submit"><Search className="mr-2 h-4 w-4" />Search</Button>
        </form>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-gray-500">
            {stats.length === 0 ? 'No invite groups yet. Admins create them from Registrations → Invite Links.' : `Nothing matches “${query}”.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">Group / institute</th>
                  <th className="px-4 py-3">Access codes</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Max allowed</th>
                  <th className="px-4 py-3 text-right">Used quantity</th>
                  {canOpenRegistrations && <th className="px-4 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(({ link }) => {
                  const codes = link.accessCodes || [];
                  const pct = link.maxUses ? Math.min(100, Math.round(((link.usedCount || 0) / link.maxUses) * 100)) : null;
                  return (
                    <tr key={link.id} className="align-top hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 font-medium text-gray-900"><Link2 className="h-4 w-4 shrink-0 text-blue-600" />{link.name}</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {link.waivePayment ? 'Free registration' : 'Paid'}{link.requireAccessCode ? ' · code required' : ''}
                        </div>
                        {link.passIds?.length > 0 && (
                          <div className="mt-0.5 text-xs text-gray-500">Passes: {link.passIds.map(passName).join(', ')}</div>
                        )}
                        {link.validUntil && <div className="mt-0.5 text-xs text-gray-500">Until {formatDateTime(link.validUntil)} IST</div>}
                      </td>
                      <td className="px-4 py-3">
                        {codes.length === 0 ? <span className="text-gray-400">—</span> : (
                          <div className="flex max-w-xs flex-wrap gap-1">
                            {codes.slice(0, 4).map((code) => (
                              <button
                                type="button"
                                key={code.code}
                                onClick={() => downloadCode(link, code)}
                                disabled={!code.usedCount || busy === `${link.id}:${code.code}`}
                                title={[code.label, code.startsAt && `from ${formatDateTime(code.startsAt)} IST`, code.endsAt && `until ${formatDateTime(code.endsAt)} IST`, code.usedCount ? `Download the ${code.usedCount} registrations that used this code (Excel)` : 'Not used yet'].filter(Boolean).join(' · ')}
                                className={`inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs disabled:cursor-default ${code.active ? 'text-gray-700' : 'text-gray-400 line-through'} ${code.usedCount ? 'hover:bg-blue-100' : ''}`}
                              >
                                {busy === `${link.id}:${code.code}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
                                {code.code}
                                <span className="font-sans tabular-nums text-gray-500">{code.usedCount || 0}{code.maxUses ? `/${code.maxUses}` : ''}</span>
                                {code.usedCount > 0 && busy !== `${link.id}:${code.code}` && <Download className="h-3 w-3" />}
                              </button>
                            ))}
                            {codes.length > 4 && <span className="text-xs text-gray-500">+{codes.length - 4} more</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${link.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {link.active ? 'Active' : 'Off'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {link.maxUses ?? 'Unlimited'}
                        {pct != null && (
                          <div className="ml-auto mt-1.5 h-1.5 w-20 overflow-hidden rounded-full bg-gray-100">
                            <div className={`h-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => download(link)}
                          disabled={!link.usedCount || busy === link.id}
                          className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 font-semibold tabular-nums text-blue-700 hover:bg-blue-100 disabled:cursor-default disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500"
                          title={link.usedCount ? `Download these ${link.usedCount} registrations (Excel)` : 'No registrations yet'}
                        >
                          {busy === link.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                          {(link.usedCount || 0).toLocaleString('en-IN')}
                          {link.usedCount > 0 && busy !== link.id && <Download className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                      {canOpenRegistrations && (
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="outline" onClick={() => navigate(registrationsUrl({ type: 'invite', value: link.id, name: link.name }))}>
                            <Eye className="mr-1 h-4 w-4" /> View details
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AccessCodesPage;
